import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';

import { Ati } from '@atlassian/ari';

import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

import { buildCreationLink } from '../../CreateContentRouteComponent/index.ts';
import { useHybridLinkClick } from '../../CreateContentPopover/CreateContentPopoverItems/hybridLinkHelper.ts';
import { ConfluencePageSubType } from '../../queries/__types__/CreateContentMutation.ts';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.liveDoc.title',
		defaultMessage: 'Live Doc',
		description: 'title indicating that this option will create a live doc',
	},
});

const CREATE_LIVE_DOC_POPOVER_ITEM = 'create-live-doc-popover-item';

export function useLiveDocItem({
	parentId,
	source,
	spaceKey,
	onClick,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();
	const { isRovoLLMEnabled } = useSessionData();
	const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
		source: CREATE_LIVE_DOC_POPOVER_ITEM,
	});

	const createHref = buildCreationLink({
		contentType: 'page',
		subType: ConfluencePageSubType.LIVE,
		parentPageId: parentId,
		withFallback: true,
		source,
		spaceKey,
	});

	const hybridLinkClick = useHybridLinkClick(createHref);
	const { openModal } = useSmartCreationModalActions();
	const { push } = useRouteActions();

	const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
		onClick('createLiveDocPopoverItem');

		// When cwr_blank_object_experiment is variant3, we
		// open CWR modal and pass in the normal creation flow as a callback
		// to fire if the user closes the modal. If the user goes through the CWR flow,
		// CWR creates the new content.
		if (isRovoLLMEnabled && expValEquals('cwr_blank_object_experiment', 'cohort', 'variant3')) {
			e?.preventDefault();
			openModal({
				entryPoint: `${source}-cwrBlankObject`,
				spaceKey,
				initialOutcomeType: 'liveDoc',
				onCloseCallback: () => push(createHref),
			});
			return;
		}

		hybridLinkClick(e);
	};

	return {
		ati: Ati.parse('ati:cloud:confluence:livedoc'),
		children: formatMessage(messages.title),
		onClick: enforceStorageLimit(handleClick),
		elemBefore: PageLiveDocIcon,
		...(!shouldBlockCreate && { href: createHref }),
		testId: `create-menu-${shouldBlockCreate ? 'button' : 'link'}-item`,
	};
}
